import {
  ComplianceDataResponse,
  PieStatusKey,
  RegulationBreakdown,
  RegulationSummary,
} from './types/dashboard.types';

type Fund = { fund_id: string; fund_name: string; fund_name_full?: string };

export function getVarExceedanceClass(varExceedance: number) {
  if (varExceedance < -1) return 'bg-green-400 text-white';
  if (varExceedance < -0.7) return 'bg-green-300 text-white';
  if (varExceedance < -0.4) return 'bg-green-200 text-gray-900';
  if (varExceedance < -0.1) return 'bg-green-100 text-gray-900';
  if (varExceedance >= 1) return 'bg-red-400 text-white';
  if (varExceedance >= 0.7) return 'bg-red-300 text-white';
  if (varExceedance >= 0.4) return 'bg-red-200 text-gray-900';
  if (varExceedance >= 0.1) return 'bg-red-100 text-gray-900';
  return 'bg-gray-100 text-gray-900';
}

export function buildSubcategoryPieData(
  counts: Record<string, number>,
): { name: string; value: number }[] {
  return [
    ...(counts.Fail ? [{ name: 'Fail', value: counts.Fail }] : []),
    ...(counts.Alert ? [{ name: 'Alert', value: counts.Alert }] : []),
    ...(counts.Pass ? [{ name: 'Pass', value: counts.Pass }] : []),
    ...(counts['N/A'] ? [{ name: 'N/A', value: counts['N/A'] }] : []),
  ];
}

export function hasSubcategoryCounts(counts: Record<string, number>): boolean {
  return Object.values(counts).some((value) => value > 0);
}

export function preparePassFailPieData(
  aggResults: Record<PieStatusKey, number>,
) {
  const total = Object.values(aggResults).reduce((sum, val) => sum + val, 0);
  if (total === 0) return null;

  const chartData = Object.entries(aggResults).map(([status, value]) => ({
    name: status,
    value,
  }));

  const passPct = Math.round((aggResults.Pass / total) * 100 || 0);

  return {
    chartData,
    passPct,
  };
}

export type FundStatusWithDate = Fund & {
  date: string;
};

export function groupStatusByRegulationInSection(
  data: ComplianceDataResponse,
  sectionId: string = 'All',
): Record<string, FundStatusWithDate[]> {
  const grouped: Record<string, FundStatusWithDate[]> = {};

  const regulationsToCheck =
    sectionId === 'All'
      ? Object.entries(data.regulation_breakdown)
      : Object.entries(data.regulation_breakdown).filter(
          ([id]) => id === sectionId,
        );

  regulationsToCheck.forEach(([regulationId, regulationData]) => {
    const chartData = regulationData.chart_data;
    if (!chartData || chartData.length === 0) return;

    // Get the latest chart data point
    const latestData = chartData[chartData.length - 1];
    if (!latestData || !latestData.data) return;

    Object.entries(latestData.data).forEach(([subcategory, statusData]) => {
      const fails = statusData.Fail?.funds ?? [];

      if (fails.length === 0) return; // ✅ skip 0-count subcategories

      if (!grouped[subcategory]) {
        grouped[subcategory] = [];
      }

      fails.forEach((fund) => {
        grouped[subcategory].push({
          fund_id: fund.fund_id,
          fund_name: fund.fund_name,
          fund_name_full: fund.fund_name_full,
          date: latestData.date,
        });
      });
    });
  });

  return grouped;
}

export type FundStatusDateType = FundStatusWithDate & {
  type: 'Fail' | 'Alert' | 'Pass';
};

export function groupAllStatusByRegulationInSection(
  data: ComplianceDataResponse,
  sectionId: string = 'All',
): Record<string, FundStatusDateType[]> {
  const grouped: Record<string, FundStatusDateType[]> = {};

  const regulationsToCheck =
    sectionId === 'All'
      ? Object.entries(data.regulation_breakdown)
      : Object.entries(data.regulation_breakdown).filter(
          ([id]) => id === sectionId,
        );

  regulationsToCheck.forEach(([regulationId, regulationData]) => {
    const chartData = regulationData.chart_data;
    if (!chartData || chartData.length === 0) return;

    // Iterate across the full time series so charts render all dates
    chartData.forEach((point) => {
      if (!point || !point.data) return;

      Object.entries(point.data).forEach(([subcategory, statusData]) => {
        (['Fail', 'Alert', 'Pass'] as const).forEach((statusType) => {
          const items = statusData[statusType]?.funds ?? [];

          if (items.length === 0) return;

          if (!grouped[subcategory]) {
            grouped[subcategory] = [];
          }

          items.forEach((fund) => {
            grouped[subcategory].push({
              fund_id: fund.fund_id,
              fund_name: fund.fund_name,
              fund_name_full: fund.fund_name_full,
              date: point.date,
              type: statusType,
            });
          });
        });
      });
    });
  });

  return grouped;
}

// TODO: This function needs to be updated for the new data structure
// Currently commented out as it's not being used
/*
export function filterDashboardDataByLookback(
  data: ComplianceDataResponse,
  lookbackInDays: number,
): ComplianceDataResponse {
  // This function needs to be rewritten for the new regulation_breakdown structure
  return data;
}
*/

export function summariseComplianceDashboardData(
  data: ComplianceDataResponse,
): Record<string, RegulationSummary> {
  const regulationSummaries: Record<string, RegulationSummary> = {};

  // Use the new regulation_breakdown structure
  Object.entries(data.regulation_breakdown).forEach(
    ([regulationId, regulationData]) => {
      if (!regulationSummaries[regulationId]) {
        regulationSummaries[regulationId] = {
          section_id: regulationId,
          section_title: regulationData.regulation_name,
          totalFunds: 0,
          totalReportedFunds: 0,
          totalAUM: 0,
          statusCounts: { Pass: 0, Alert: 0, Fail: 0 },
          subsectionCounts: {},
        };
      }

      const sectionSummary = regulationSummaries[regulationId];

      sectionSummary.totalReportedFunds =
        regulationData.reported_fund_count ?? 0;
      sectionSummary.totalFunds = regulationData.total_fund_count ?? 0;
      sectionSummary.totalAUM = regulationData.regulation_aum ?? 0;
      sectionSummary.statusCounts = {
        Pass: regulationData.status_count.total.Pass ?? 0,
        Alert: regulationData.status_count.total.Alert ?? 0,
        Fail: regulationData.status_count.total.Fail ?? 0,
      };

      // Add subsection (subcategory-level) status breakdown
      if (regulationData.status_count.by_subcategory) {
        Object.entries(regulationData.status_count.by_subcategory).forEach(
          ([subcategoryName, counts]) => {
            sectionSummary.subsectionCounts![subcategoryName] = {
              Pass: counts.Pass ?? 0,
              Alert: counts.Alert ?? 0,
              Fail: counts.Fail ?? 0,
              ...(counts['N/A'] != null ? { 'N/A': counts['N/A'] } : {}),
            };
          },
        );
      }
    },
  );

  return regulationSummaries;
}

export function filterStatusByFundId(
  data: Record<string, FundStatusWithDate[]>,
  fundId: string | undefined,
): Record<string, FundStatusWithDate[]> {
  const filtered: Record<string, FundStatusWithDate[]> = {};

  if (!fundId) {
    return data;
  }

  Object.entries(data).forEach(([subcategory, entries]) => {
    const matches = entries
      .filter((entry) => entry.fund_id === fundId)
      .map((entry) => ({ ...entry })); // avoid mutation issues

    if (matches.length > 0) {
      filtered[subcategory] = matches;
    }
  });

  return filtered;
}

export function extractUniqueFunds(
  data: Record<string, { fund_id: string; fund_name: string; date: string }[]>,
): FundStatusWithDate[] {
  const fundMap = new Map<string, FundStatusWithDate>();

  for (const section of Object.values(data)) {
    for (const { fund_id, fund_name, date } of section) {
      // Use fund_name as the key to allow same fund_id with different fund_name
      if (!fundMap.has(fund_name)) {
        fundMap.set(fund_name, { fund_id, fund_name, date });
      }
    }
  }

  return Array.from(fundMap.values());
}
